import { Assert } from "../../Assert";
import { DataSource } from "../../Data/DataSource";
import { Nullable, Nullish } from "../../Extensions";
import { ArrayHelper } from "../../Helpers/ArrayHelper";
import { ObjectHelper } from "../../Helpers/ObjectHelper";
import { StringHelper } from "../../Helpers/StringHelper";
import { LinqArray } from "../../LinqArray";
import { PropertyChange } from "../../PropertyChange";
import { IRadObject, Property, RadObject } from "../../RadObject";
import { WindowShim } from "../../RadWindow";
import { ClientAwareEntityIdAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework.Data.Entities/ClientAwareEntityIdAttribute";
import { ClientAwareLookupItemAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework.Data.Entities/ClientAwareLookupItemAttribute";
import { PropertyEvaluator } from "../../Server/DotNet/SeriouslyRAD.Framework.Data.Entities/Evaluation/Evaluators/PropertyEvaluator";
import { ClientAwareCategoryAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareCategoryAttribute";
import { ClientAwareDateTimeAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareDateTimeAttribute";
import { ClientAwareDecimalAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareDecimalAttribute";
import { ClientAwareDescriptionAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareDescriptionAttribute";
import { ClientAwareDisplayNameAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareDisplayNameAttribute";
import { ClientAwareHelpTextAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareHelpTextAttribute";
import { ClientAwareMandatoryEvaluatorAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareMandatoryEvaluatorAttribute";
import { ClientAwareOrderAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareOrderAttribute";
import { ClientAwarePropertyInfoAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwarePropertyInfoAttribute";
import { ClientAwareReadOnlyAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareReadOnlyAttribute";
import { ClientAwareRelevantEvaluatorAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareRelevantEvaluatorAttribute";
import { ClientAwareStringLengthAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareStringLengthAttribute";
import { IPropertyInfo } from "../../Server/DotNet/SeriouslyRAD.Framework/Reflection/PropertyInfo";
import { ITypeInfo, TypeInfo } from "../../Server/DotNet/SeriouslyRAD.Framework/Reflection/TypeInfo";
import { BuiltInTypes } from "../../Server/DotNet/System/BuiltInTypes";
import { Nullable as DotNetNullable } from "../../Server/DotNet/System/Nullable";
import { Accordion } from "./Accordion";
import { AccordionPanel } from "./AccordionPanel";
import { CheckBoxListFlags } from "./CheckBoxListFlags";
import { ComboBox } from "./ComboBox";
import { ComboBoxLookupItem } from "./ComboBoxLookupItem";
import { ComboBoxYesNo } from "./ComboBoxYesNo";
import { ContainerControl } from "./ContainerControl";
import { Date } from "./Date";
import { DateTime } from "./DateTime";
import { EntityPicker } from "./EntityPicker";
import { IInputControl } from "./InputControl";
import { NumericUpDown } from "./NumericUpDown";
import { PropertyEditor } from "./PropertyEditor";
import { PropertyEditorAttribute } from "./PropertyEditorAttribute";
import { TextBox, TextBoxTypeEnum } from "./TextBox";
import { Time } from "./Time";
import { TimeSpan } from "./TimeSpan";

const
    radWindow = WindowShim(),
    editorControlOverrides: ((propertyInfo: IPropertyInfo) => Promise<IInputControl | undefined>)[] = [];

type DesignablePropertyInfo = {
    category: Nullish<string>;
    classHierarchyDepth: Nullish<number>;
    displayName: string;
    helpText: Nullish<string>;
    order: Nullish<number>;
    propertyName: string;
};

export class ClassEditor extends ContainerControl
{
    // #region Static

    // #region Public Methods

    public static async createAsync(radObject: IRadObject): Promise<ClassEditor>
    public static async createAsync(typeId: string): Promise<ClassEditor>
    public static async createAsync(typeInfo: ITypeInfo): Promise<ClassEditor>
    public static async createAsync(radObjectOrTypeIdOrTypeInfo: IRadObject | string | ITypeInfo): Promise<ClassEditor>
    public static async createAsync(radObjectOrTypeIdOrTypeInfo: IRadObject | string | ITypeInfo): Promise<ClassEditor>
    {
        return await this._createAsync(radObjectOrTypeIdOrTypeInfo);
    }
    public static registerOverride(editorControlOverride: (propertyInfo: IPropertyInfo) => Promise<IInputControl | undefined>)
    {
        editorControlOverrides.push(editorControlOverride);
    }

    // #endregion

    // #region Private Methods

    private static async _createAsync(radObjectOrTypeIdOrTypeInfo: IRadObject | string | ITypeInfo, dataSourcePropertyPath?: string, dataSource?: DataSource): Promise<ClassEditor>
    {
        dataSource = dataSource ?? new DataSource({ data: {} });

        let
            radObject: RadObject | undefined,
            typeInfo: ITypeInfo | undefined;

        if (radObjectOrTypeIdOrTypeInfo instanceof TypeInfo)
            typeInfo = radObjectOrTypeIdOrTypeInfo;
        else if (radObjectOrTypeIdOrTypeInfo instanceof RadObject)
        {
            radObject = radObjectOrTypeIdOrTypeInfo;
            typeInfo = TypeInfo.forRadObject(radObjectOrTypeIdOrTypeInfo);
        }
        else if (typeof radObjectOrTypeIdOrTypeInfo === "string")
        {
            const
                typeId = radObjectOrTypeIdOrTypeInfo,
                typeInfoMetadata = await TypeInfo.loadTypeInfoMetadataFromServerAsync(typeId);

            if (!typeInfoMetadata)
                Assert.isNotNullOrUndefined(typeInfoMetadata, `Unable to get type info metadata for type \"${typeId}\"`);

            try
            {
                radWindow.fullPageLoader?.enqueue();
                typeInfo = await TypeInfo.fromTypeInfoMetadataAsync(typeInfoMetadata);
            }
            finally { radWindow.fullPageLoader?.dequeue(); }
        }
        else
            throw new Error("Not implemented");

        const
            designablePropertyInfos: LinqArray<DesignablePropertyInfo> = LinqArray.create();

        for (const propertyName in typeInfo.properties)
        {
            const
                property = typeInfo.properties[propertyName],
                displayNameAttribute = property.getAttribute(ClientAwareDisplayNameAttribute);

            if (!displayNameAttribute)
                continue;

            designablePropertyInfos.push({
                category: property.getAttribute(ClientAwareCategoryAttribute)?.category ?? null,
                classHierarchyDepth: property.getAttribute(ClientAwarePropertyInfoAttribute)?.classHierarchyDepth ?? null,
                displayName: displayNameAttribute.displayName ?? propertyName,
                helpText: property.getAttribute(ClientAwareHelpTextAttribute)?.helpText ?? null,
                order: property.getAttribute(ClientAwareOrderAttribute)?.order ?? null,
                propertyName: propertyName,
            });
        }

        const
            classEditor = new ClassEditor(),
            accordion = !dataSourcePropertyPath ? new Accordion({ allowMultiplePanelsOpen: true }) : undefined,
            orderedDesignablePropertyInfos =
                designablePropertyInfos
                    .orderBy(designablePropertyInfo => designablePropertyInfo.category)
                    .thenBy(designablePropertyInfo => designablePropertyInfo.classHierarchyDepth ?? Number.MAX_SAFE_INTEGER)
                    .thenBy(designablePropertyInfo => designablePropertyInfo.order)
                    .thenBy(designablePropertyInfo => designablePropertyInfo.displayName);

        classEditor._typeInfo = typeInfo;

        if (accordion)
            classEditor.$$element.get()?.append(accordion.$$element.get());

        for (const designablePropertyInfo of orderedDesignablePropertyInfos)
        {
            const
                propertyInfo = typeInfo.properties[designablePropertyInfo.propertyName],
                categoryName = designablePropertyInfo.category ? designablePropertyInfo.category : StringHelper.empty,
                propertyEditor = (await this._createPropertyEditorAsync(propertyInfo, designablePropertyInfo.propertyName, dataSourcePropertyPath, dataSource));

            if (accordion)
            {
                const
                    accordionPanel =
                        ArrayHelper.firstOrDefault(accordion.controlsGet(), item => item.text.get() === categoryName)
                        ?? new AccordionPanel({ text: categoryName }).appendTo(accordion);

                propertyEditor.appendTo(accordionPanel);
            }
            else
                propertyEditor.appendTo(classEditor);

            if (radObject)
            {
                const
                    dataSourcePath = (!!dataSourcePropertyPath ? `${dataSourcePropertyPath}.` : StringHelper.empty) + propertyInfo.name,
                    value = (radObject as any)[propertyInfo.name]?.get() ?? null;

                dataSource.propertyValueByPathSet(dataSourcePath, value);
            }
        }

        return classEditor;
    }
    private static async _createEditorControlAsync(propertyInfo: IPropertyInfo, dataSource: DataSource): Promise<IInputControl | ClassEditor>
    {
        let
            editorControl: Nullable<IInputControl | ClassEditor> = null;

        for (const editorControlOverride of editorControlOverrides.reverse())
        {
            editorControl = await editorControlOverride(propertyInfo) ?? null;

            if (editorControl)
                break;
        }

        const
            propertyTypeId =
                DotNetNullable.getUnderlyingType(propertyInfo.propertyTypeId as BuiltInTypes)
                ?? propertyInfo.propertyTypeId;

        if (!editorControl && this._isEnumerable(propertyTypeId))
            editorControl = new ClassEditorEnumerable();

        if (!editorControl && this._isDictionary(propertyTypeId))
            editorControl = new ClassEditorDictionary();

        if (!editorControl)
        {
            switch (propertyTypeId as BuiltInTypes)
            {
                case "System.Boolean, System.Private.CoreLib":
                    editorControl = new ComboBoxYesNo();
                    break;
                case "System.Byte[], System.Private.CoreLib":
                case "System.Char, System.Private.CoreLib":
                case "System.SByte[], System.Private.CoreLib":
                case "System.String, System.Private.CoreLib":
                    {
                        const
                            stringLengthAttribute = propertyInfo.getAttribute(ClientAwareStringLengthAttribute);

                        if (stringLengthAttribute)
                            editorControl = new TextBox({ maximumLength: stringLengthAttribute.maximumLength });
                        else
                            editorControl = new TextBox({ type: TextBoxTypeEnum.multiline });

                        break;
                    }
                case "System.DateOnly, System.Private.CoreLib":
                    editorControl = new Date();
                    break;
                case "System.DateTime, System.Private.CoreLib":
                    {
                        const
                            dateTimeAttribute = propertyInfo.getAttribute(ClientAwareDateTimeAttribute);

                        editorControl = new DateTime({ isLocal: dateTimeAttribute?.isLocal ?? false });
                        break;
                    }
                case "System.Decimal, System.Private.CoreLib":
                case "System.Double, System.Private.CoreLib":
                case "System.Int16, System.Private.CoreLib":
                case "System.Int32, System.Private.CoreLib":
                case "System.Int64, System.Private.CoreLib":
                case "System.Single, System.Private.CoreLib":
                case "System.UInt16, System.Private.CoreLib":
                case "System.UInt32, System.Private.CoreLib":
                case "System.UInt64, System.Private.CoreLib":
                    {
                        const
                            decimalAttribute = propertyInfo.getAttribute(ClientAwareDecimalAttribute);

                        editorControl = new NumericUpDown({
                            currencySymbol: decimalAttribute?.currencySymbol,
                            precision: decimalAttribute?.precision,
                            scale: decimalAttribute?.scale,
                        });

                        break;
                    }
                case "System.Guid, System.Private.CoreLib":
                    {
                        const
                            entityIdAttribute = propertyInfo.getAttribute(ClientAwareEntityIdAttribute);

                        if (entityIdAttribute)
                        {
                            editorControl = new EntityPicker({
                                entityName: entityIdAttribute.entityName,
                                entityNameFriendly: entityIdAttribute.entityNameFriendly,
                                entitySetName: entityIdAttribute.entityNamePlural,
                                entitySetNameFriendly: entityIdAttribute.entityNamePluralFriendly,
                                entityDescriptionProperty:
                                    entityIdAttribute.entityDescriptionProperty
                                        ? StringHelper.toLowerCamelCase(entityIdAttribute.entityDescriptionProperty)
                                        : null,
                                entityFilter: entityIdAttribute.entityFilter,
                            });
                            break;
                        }

                        const
                            lookupItemAttribute = propertyInfo.getAttribute(ClientAwareLookupItemAttribute);

                        if (lookupItemAttribute)
                        {
                            editorControl = new ComboBoxLookupItem({ lookupType: lookupItemAttribute.typeDescription, });
                            break;
                        }

                        editorControl = new TextBox();
                        break;
                    }
                case "System.TimeOnly, System.Private.CoreLib":
                    editorControl = new Time();
                    break;
                case "System.TimeSpan, System.Private.CoreLib":
                    editorControl = new TimeSpan();
                    break;
                case "System.Object, System.Private.CoreLib":
                    console.error(`Property type \"${propertyInfo.propertyTypeId}\" has not been implemented`, propertyInfo);
                    throw new Error(`Property type \"${propertyInfo.propertyTypeId}\" has not been implemented`);
                default:
                    {
                        const
                            typeInfoMetadata = await TypeInfo.loadTypeInfoMetadataFromServerAsync(propertyTypeId);

                        if (typeInfoMetadata?.isEnum)
                        {
                            const
                                typeInfo = await TypeInfo.fromTypeInfoMetadataAsync(typeInfoMetadata);

                            if (typeInfoMetadata.hasEnumFlags)
                            {
                                const
                                    items =
                                        Object.values(typeInfo.enumValues ?? {})
                                            .map(item => ({
                                                value: item.value,
                                                description:
                                                    item.attributes
                                                        ?.filter((item): item is ClientAwareDescriptionAttribute => item instanceof ClientAwareDescriptionAttribute)
                                                        .find(d => d != null)
                                                        ?.description
                                                    ?? item.name,
                                            }));

                                editorControl = new CheckBoxListFlags({
                                    listDataSource: new DataSource({ data: items }),
                                });
                            }
                            else
                            {
                                const
                                    items =
                                        Object.values(typeInfo.enumValues ?? {})
                                            .map(item => ({
                                                value: StringHelper.toLowerCamelCase(item.name),
                                                description:
                                                    item.attributes
                                                        ?.filter((item): item is ClientAwareDescriptionAttribute => item instanceof ClientAwareDescriptionAttribute)
                                                        .find(d => d != null)
                                                        ?.description
                                                    ?? item.name,
                                            }));

                                editorControl = new ComboBox({
                                    listDataSource: new DataSource({ data: items }),
                                });
                            }
                        }
                        else
                            editorControl = await this._createAsync(propertyTypeId, propertyInfo.name, dataSource);

                        break;
                    }
            }
        }

        return editorControl;
    }
    private static async _createPropertyEditorAsync(propertyInfo: IPropertyInfo, propertyName: string, dataSourcePropertyPath: string | undefined, dataSource: DataSource)
    {
        const
            displayNameAttribute = propertyInfo.getAttribute(ClientAwareDisplayNameAttribute),
            helpTextAttribute = propertyInfo.getAttribute(ClientAwareHelpTextAttribute),
            mandatoryEvaluatorAttribute = propertyInfo.getAttribute(ClientAwareMandatoryEvaluatorAttribute),
            propertyEditorAttribute = propertyInfo.getAttribute(PropertyEditorAttribute),
            readOnlyAttribute = propertyInfo.getAttribute(ClientAwareReadOnlyAttribute),
            relevantEvaluatorAttribute = propertyInfo.getAttribute(ClientAwareRelevantEvaluatorAttribute),
            editorControl = propertyEditorAttribute ? propertyEditorAttribute.createInputControl() : await this._createEditorControlAsync(propertyInfo, dataSource);

        if (editorControl instanceof ClassEditor)
            return editorControl;

        const
            mandatoryEvaluator = mandatoryEvaluatorAttribute?.evaluator?.clone() ?? null,
            relevantEvaluator = relevantEvaluatorAttribute?.evaluator?.clone() ?? null,
            evaluators = [];

        if (mandatoryEvaluator)
            evaluators.push(mandatoryEvaluator);

        if (relevantEvaluator)
            evaluators.push(relevantEvaluator);

        evaluators
            .forEach(item =>
            {
                item
                    .flatten()
                    .filter((itemInner): itemInner is PropertyEvaluator => itemInner instanceof PropertyEvaluator)
                    .forEach(
                        itemInner =>
                        {
                            let
                                propertyName = itemInner.propertyName.get();

                            if (propertyName && propertyName.indexOf(".") === -1)
                                propertyName = StringHelper.toLowerCamelCase(propertyName);

                            itemInner.propertyName.set((!!dataSourcePropertyPath ? `${dataSourcePropertyPath}.` : StringHelper.empty) + propertyName)
                        }
                    );
            });

        return new PropertyEditor({
            dataSource: dataSource,
            dataSourceProperty: (!!dataSourcePropertyPath ? `${dataSourcePropertyPath}.` : StringHelper.empty) + propertyName,
            editMode: true,
            editorControl,
            helpText: helpTextAttribute?.helpText ?? null,
            mandatoryEvaluator: mandatoryEvaluator,
            propertyDisplayName: displayNameAttribute?.displayName ?? propertyName,
            readOnly: !!readOnlyAttribute,
            relevantEvaluator: relevantEvaluator,
        });
    }
    private static _isDictionary(propertyTypeId: string)
    {
        return (
            propertyTypeId.startsWith("System.Collections.Generic.Dictionary")
            || propertyTypeId.startsWith("System.Collections.Generic.IDictionary")
        );
    }
    private static _isEnumerable(propertyTypeId: string)
    {
        return (
            propertyTypeId.startsWith("System.Collections.Generic.IEnumerable")
            || propertyTypeId.startsWith("System.Collections.Generic.IList")
            || propertyTypeId.startsWith("System.Collections.Generic.List")
        );
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    private _typeInfo?: ITypeInfo;

    // #endregion

    // #region Public Methods

    public load(radObjectOrObject: IRadObject | Object)
    {
        const
            typeInfo = this._typeInfo;

        Assert.isNotNullOrUndefined(typeInfo, "typeInfo");

        const
            dataSource = new DataSource({ data: {} });

        if (radObjectOrObject instanceof RadObject)
            this._processDesignableProperties(radObjectOrObject, (property, propertyName) => dataSource.propertyValueByPathSet(propertyName, ObjectHelper.cloneObject(property.get())));
        else
        {
            const
                objectDataSource = new DataSource({ data: radObjectOrObject });

            this.controlsGet(true, PropertyEditor)
                .forEach(item =>
                {
                    const
                        dataSourcePropertyPropertyEditor = item.dataSourceProperty.get();

                    if (!dataSourcePropertyPropertyEditor)
                        return;

                    dataSource.propertyValueByPathSet(dataSourcePropertyPropertyEditor, ObjectHelper.cloneObject(objectDataSource.propertyValueByPathGet(dataSourcePropertyPropertyEditor)));
                });
        }

        this.dataSource.set(dataSource);
    }
    public save(radObjectOrObject: IRadObject | Object)
    {
        const
            dataSource = this.dataSource.get();

        if (!dataSource)
            return;

        if (radObjectOrObject instanceof RadObject)
            this._processDesignableProperties(radObjectOrObject, (property, propertyName) => property.set(dataSource.propertyValueByPathGet(propertyName) ?? null));
        else
        {
            const
                objectDataSource = new DataSource({ data: radObjectOrObject });

            this.controlsGet(true, PropertyEditor)
                .forEach(item =>
                {
                    const
                        dataSourcePropertyPropertyEditor = item.dataSourceProperty.get();

                    if (!dataSourcePropertyPropertyEditor)
                        return;

                    objectDataSource.propertyValueByPathSet(dataSourcePropertyPropertyEditor, ObjectHelper.cloneObject(dataSource.propertyValueByPathGet(dataSourcePropertyPropertyEditor)));
                });
        }
    }
    public validateMandatoryProperties(): string[]
    {
        const
            typeInfo = this._typeInfo;

        Assert.isNotNullOrUndefined(typeInfo, "typeInfo");

        const
            validationErrors: string[] = [],
            dataSource = this.dataSource.get();

        if (!dataSource)
            return validationErrors;

        for (const propertyName in typeInfo.properties)
        {
            const
                property = typeInfo.properties[propertyName],
                mandatoryEvaluator = property.attributes?.find(item => item instanceof ClientAwareMandatoryEvaluatorAttribute)?.evaluator?.clone();

            mandatoryEvaluator?.context.set(dataSource);

            if (!mandatoryEvaluator || !mandatoryEvaluator.evaluate())
                continue;

            const
                relevantEvaluator = property.attributes?.find(item => item instanceof ClientAwareRelevantEvaluatorAttribute)?.evaluator?.clone();

            relevantEvaluator?.context.set(dataSource);

            if (relevantEvaluator && !relevantEvaluator.evaluate())
                continue;

            const
                propertyDisplayName = property.attributes?.find(item => item instanceof ClientAwareDisplayNameAttribute)?.displayName ?? property.name,
                propertyValue = dataSource.propertyValueByPathGet(propertyName);

            if (ObjectHelper.isNullOrUndefined(propertyValue))
                validationErrors.push(`${propertyDisplayName} is required.`);
        }

        return validationErrors;
    }

    // #endregion

    // #region Private Methods

    private _processDesignableProperties(radObject: IRadObject, action: (property: Property, propertyName: string) => void)
    {
        const
            typeInfo = this._typeInfo;

        Assert.isNotNullOrUndefined(typeInfo, "typeInfo");

        for (const propertyName in typeInfo.properties)
        {
            const
                propertyInfo = typeInfo.properties[propertyName],
                displayNameAttribute = propertyInfo.getAttribute(ClientAwareDisplayNameAttribute);

            if (!displayNameAttribute)
                continue;

            const
                property = (radObject as any)[propertyName];

            if (property instanceof Property)
                action(property, propertyName);
        }
    }

    // #endregion

    // #region Overrides

    protected override _dataSourceChanged(change: PropertyChange<DataSource>)
    {
        super._dataSourceChanged(change);

        this.controlsGet(true)
            .filter((item): item is PropertyEditor => item instanceof PropertyEditor)
            .forEach(item => item.dataSource.set(change.currentValue));
    }

    // #endregion

    // #endregion
}

class ClassEditorDictionary extends ClassEditor
{

}

class ClassEditorEnumerable extends ClassEditor
{

}
